import { statusColors } from "../../theme/statusPalette";
import { getWorkorderStatusStyle } from "../data/workorder-status/workorderStatusStyles";
import { getReplenishmentStatusStyle } from "../data/replenishment-status/ReplenishmentStatusBadge";
import { getReplenishmentPriorityStyle } from "../data/replenishment-priority/ReplenishmentPriorityBadge";
import { getQuotationStatusStyle } from "../data/quotation-status/quotationStatusStyles";
import colorsJson from "../../services/colors/colors.json";

export interface FilterBadgeStyle {
  soft: string;
  mid: string;
  text: string;
}

const PALETTE = statusColors;

/**
 * getFilterBadgeStyle: Résout le style pastel (fond soft 100, croix mid 400, texte 800)
 * pour n'importe quel filtre et entité de l'application.
 */
export const getFilterBadgeStyle = (
  type: string,
  value?: any,
  entity?: string
): FilterBadgeStyle => {
  switch (type) {
    case "status": {
      const ent = entity || "workorder";
      const statusStr = value ? String(value).toUpperCase() : "";
      if (ent === "replenishment") {
        const repStyle = getReplenishmentStatusStyle(statusStr);
        return { soft: repStyle.soft, mid: repStyle.mid, text: repStyle.text };
      }
      if (ent === "quotation") {
        const qStyle = getQuotationStatusStyle(statusStr);
        return { soft: qStyle.soft, mid: qStyle.mid, text: qStyle.text };
      }
      // Par défaut (workorder, planning, etc.)
      const woStyle = getWorkorderStatusStyle(statusStr);
      return { soft: woStyle.soft, mid: woStyle.mid, text: woStyle.text };
    }

    case "safetyDiagStatus": {
      const statusStr = value ? String(value).toUpperCase() : "";
      if (statusStr === "VALID") {
        return {
          soft: PALETTE["pastel-green"][100],
          mid: PALETTE["pastel-green"][400],
          text: PALETTE["pastel-green"][800],
        };
      }
      if (statusStr === "INVALID") {
        return {
          soft: PALETTE.red[100],
          mid: PALETTE.red[400],
          text: PALETTE.red[800],
        };
      }
      return {
        soft: PALETTE["navy-blue"][100],
        mid: PALETTE["navy-blue"][400],
        text: PALETTE["navy-blue"][800],
      };
    }

    case "priority": {
      const pStyle = getReplenishmentPriorityStyle(value);
      return { soft: pStyle.soft, mid: pStyle.mid, text: pStyle.text };
    }

    case "type": {
      const typeStr = String(value || "").toUpperCase();
      // Type client (ACTIVE, PROSPECT, INACTIVE)
      if (typeStr === "ACTIVE") {
        return {
          soft: PALETTE["pastel-green"][100],
          mid: PALETTE["pastel-green"][400],
          text: PALETTE["pastel-green"][800],
        };
      }
      if (typeStr === "PROSPECT") {
        return {
          soft: PALETTE.yellow[100],
          mid: PALETTE.yellow[400],
          text: PALETTE.yellow[800],
        };
      }
      if (typeStr === "INACTIVE") {
        return {
          soft: PALETTE.red[100],
          mid: PALETTE.red[400],
          text: PALETTE.red[800],
        };
      }
      // Type service (SERVICE, BOOKING_FEES, COMBO)
      if (typeStr === "SERVICE") {
        return {
          soft: PALETTE["blue-chronoflex"][100],
          mid: PALETTE["blue-chronoflex"][400],
          text: PALETTE["blue-chronoflex"][800],
        };
      }
      if (typeStr === "BOOKING_FEES") {
        return {
          soft: PALETTE["purple-heart"][100],
          mid: PALETTE["purple-heart"][400],
          text: PALETTE["purple-heart"][800],
        };
      }
      if (typeStr === "COMBO") {
        return {
          soft: PALETTE.eden[100],
          mid: PALETTE.eden[400],
          text: PALETTE.eden[800],
        };
      }
      return {
        soft: PALETTE["navy-blue"][100],
        mid: PALETTE["navy-blue"][400],
        text: PALETTE["navy-blue"][800],
      };
    }

    case "label":
    case "firstName":
    case "lastName":
      return {
        soft: PALETTE["blue-chronoflex"][100],
        mid: PALETTE["blue-chronoflex"][400],
        text: PALETTE["blue-chronoflex"][800],
      };

    case "ref":
    case "workorderType":
    case "nip":
    case "id":
      return {
        soft: PALETTE["navy-blue"][100],
        mid: PALETTE["navy-blue"][400],
        text: PALETTE["navy-blue"][800],
      };

    case "customer":
    case "name":
      return {
        soft: PALETTE["brilliant-rose"][100],
        mid: PALETTE["brilliant-rose"][400],
        text: PALETTE["brilliant-rose"][800],
      };

    case "contact":
    case "email":
    case "phone":
    case "qrCode":
      return {
        soft: PALETTE.eden[100],
        mid: PALETTE.eden[400],
        text: PALETTE.eden[800],
      };

    case "technician":
    case "createdFor": {
      const techColor =
        value?.attributes?.color || (typeof value === "object" ? value?.color : null);
      if (techColor && typeof techColor === "string") {
        const hex = techColor.toLowerCase();
        const jsonMatch = (colorsJson as Record<string, Record<string, string>>)[hex];
        if (jsonMatch) {
          return {
            soft: jsonMatch["100"] || PALETTE.yellow[100],
            mid: jsonMatch["400"] || PALETTE.yellow[400],
            text: jsonMatch["800"] || PALETTE.yellow[800],
          };
        }
        const paletteFamily = PALETTE[techColor as keyof typeof PALETTE];
        if (paletteFamily) {
          return {
            soft: paletteFamily[100],
            mid: paletteFamily[400],
            text: paletteFamily[800],
          };
        }
      }
      return {
        soft: PALETTE.yellow[100],
        mid: PALETTE.yellow[400],
        text: PALETTE.yellow[800],
      };
    }

    case "color":
      return {
        soft: PALETTE.yellow[100],
        mid: PALETTE.yellow[400],
        text: PALETTE.yellow[800],
      };

    case "van":
    case "workorderOrigin":
    case "externalId":
      return {
        soft: PALETTE["purple-heart"][100],
        mid: PALETTE["purple-heart"][400],
        text: PALETTE["purple-heart"][800],
      };

    case "odooNotSynced":
    case "missingPurchaseOrder":
    case "purchaseOrderMandatory":
      return {
        soft: PALETTE["international-orange"][100],
        mid: PALETTE["international-orange"][400],
        text: PALETTE["international-orange"][800],
      };

    case "disabled":
      return {
        soft: PALETTE.red[100],
        mid: PALETTE.red[400],
        text: PALETTE.red[800],
      };

    case "dateRange":
    case "date":
    case "scheduledDate":
    case "visitDate":
    case "visitScheduled":
    case "startDate":
    case "endDate":
    case "scheduled":
    case "realTime":
    default:
      return {
        soft: PALETTE["chrono-gray"][100],
        mid: PALETTE["chrono-gray"][400],
        text: PALETTE["chrono-gray"][800],
      };
  }
};
